import Url from 'url-parse'
import { obj2string } from '@base/lang'
import { emitter } from '@/emitter'

export function concurrentQueue<T>(
  taskList: Array<() => Promise<T>>,
  maxNum: number
): Promise<T[]> {
  if (!taskList.length) return Promise.resolve([])

  return new Promise<T[]>((resolve, reject) => {
    let nextIndex = 0
    let count = 0
    const resultList: Array<T | null> = []
    const errorList: Array<unknown> = []
    async function _run() {
      const i = nextIndex
      try {
        const task = taskList[nextIndex]
        ++nextIndex
        const res = await task()
        resultList[i] = res
        errorList[i] = null
      } catch (error) {
        resultList[i] = null
        errorList[i] = error
      }
      if (nextIndex < taskList.length) {
        _run()
      }
      count++
      if (count === taskList.length) {
        const status = !resultList.some((item) => item == null)
        if (status) {
          resolve(resultList)
        } else {
          reject(errorList)
        }
      }
    }
    for (let i = 0; i < Math.min(taskList.length, maxNum); i++) {
      _run()
    }
  })
}

type WebpFeature = 'lossy' | 'lossless' | 'alpha' | 'animation'
export function checkWebpFeature(feature: WebpFeature) {
  const kTestImages = {
    lossy: 'UklGRiIAAABXRUJQVlA4IBYAAAAwAQCdASoBAAEADsD+JaQAA3AAAAAA',
    lossless: 'UklGRhoAAABXRUJQVlA4TA0AAAAvAAAAEAcQERGIiP4HAA==',
    alpha:
      'UklGRkoAAABXRUJQVlA4WAoAAAAQAAAAAAAAAAAAQUxQSAwAAAARBxAR/Q9ERP8DAABWUDggGAAAABQBAJ0BKgEAAQAAAP4AAA3AAP7mtQAAAA==',
    animation:
      'UklGRlIAAABXRUJQVlA4WAoAAAASAAAAAAAAAAAAQU5JTQYAAAD/////AABBTk1GJgAAAAAAAAAAAAAAAAAAAGQAAABWUDhMDQAAAC8AAAAQBxAREYiI/gcA',
  }
  return new Promise<boolean>((resolve) => {
    const img = new Image()
    img.onload = function () {
      const result = img.width > 0 && img.height > 0
      resolve(result)
    }
    img.onerror = function () {
      resolve(false)
    }
    img.src = `data:image/webp;base64,${kTestImages[feature]}`
  })
}
export async function checkWebpEnable() {
  const lossyFeature = await checkWebpFeature('lossy')
  const alphaFeature = await checkWebpFeature('alpha')

  return lossyFeature === true && alphaFeature === true
}
export const webpIsEnable$ = checkWebpEnable()

export function checkFileExtension(url: string) {
  let ext = ''
  let pathname = ''
  try {
    pathname = new URL(url).pathname
    ext = getExt(pathname)
  } catch (error) {}
  return [
    ext,
    (newExt: string) => {
      if (!pathname) return url

      return `${url.slice(0, url.length - ext.length)}${newExt}`
    },
  ] as const

  function getExt(pathname: string) {
    const extension = pathname.split('.').pop()
    return extension.includes('/') ? '' : extension
  }
}

export function isObject(v: unknown): v is Record<string, unknown> {
  return Object.prototype.toString.call(v) === '[object Object]'
}

export function timeZeroFill(n: number) {
  return n < 10 ? `0${n}` : String(n)
}

export function getUrlParam(
  name: string,
  url: string = window.location.href
): string | undefined {
  const _url = new Url(url, true)
  return _url.query[name]
}

export function rpx($size: number) {
  return `${$size / 6.4}rem`
}

export function delay(ms: number = 2000): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
}

let platform: string
export function getPlatform() {
  const os = window.screenDetectResult.os
  const isMobile = checkMobile()
  const deviceType = isMobile ? 'mobile' : 'pc'
  if (deviceType === 'mobile') {
    platform = os
  } else {
    platform = 'pc'
  }
  return platform
}

export function checkMobile() {
  return ['iOS', 'Android'].includes(window.screenDetectResult.os)
}

export function throwError(err: unknown, msgTemplate?: string) {
  let _err
  if (err instanceof Error) {
    _err = err
  } else {
    let msg = ''
    if (typeof msgTemplate === 'string' && msgTemplate.length > 0) {
      msg = msgTemplate.replace('<%= desc %>', obj2string(err))
    } else {
      msg = obj2string(err)
    }
    _err = new Error(msg)
  }
  if (
    window.onemtMonitor &&
    window.onemtMonitor.errorReport instanceof Function
  ) {
    window.onemtMonitor.errorReport(_err)
  }
  console.error(_err)
  return _err
}

function getDateData(date: Date, name: string, baseUTC = false): number {
  const m = baseUTC ? 'UTC' : ''
  // @ts-ignore
  return date[`get${m}${name}`]()
}
export function getTimestr(
  _date: unknown,
  type: 'date' | 'date-time',
  baseUTC = true
): string {
  let date: Date
  if (typeof _date === 'number') {
    date = new Date(_date)
  } else {
    date = _date as Date
  }
  let day = getDateData(date, 'Date', baseUTC).toString()
  if (day.length === 1) day = `0${day}`
  let mou = (getDateData(date, 'Month', baseUTC) + 1).toString()
  if (mou.length === 1) mou = `0${mou}`
  let yea = getDateData(date, 'FullYear', baseUTC).toString()
  let hou = getDateData(date, 'Hours', baseUTC).toString()
  if (hou.length === 1) hou = `0${hou}`
  let min = getDateData(date, 'Minutes', baseUTC).toString()
  if (min.length === 1) min = `0${min}`
  let sen = getDateData(date, 'Seconds', baseUTC).toString()
  if (sen.length === 1) sen = `0${sen}`

  if (type === 'date-time') {
    return `${yea}/${mou}/${day} ${hou}:${min}:${sen}(UTC+0)`
  } else if (type === 'date') {
    return `${yea}/${mou}/${day}`
  }
}

export function go2youtube() {
  const url = 'https://www.youtube.com/channel/UC01_lHzl1COS2hKcuJUSnGA'
  if (getPlatform() === 'pc') {
    window.open(url)
  } else {
    window.location.href = url
  }
}

export function go2facebook() {
  const url = 'https://www.facebook.com/WarofEvolution'
  if (getPlatform() === 'pc') {
    window.open(url)
  } else {
    window.location.href = url
  }
}

export function go2instagram() {
  const url = 'https://www.instagram.com/warofevolution/'
  if (getPlatform() === 'pc') {
    window.open(url)
  } else {
    window.location.href = url
  }
}

export function go2x() {
  const url = 'https://x.com/'
  if (getPlatform() === 'pc') {
    window.open(url)
  } else {
    window.location.href = url
  }
}

export function download(zoneId: Zone['zoneId']) {
  const urlMap = {
    0: 'https://c2tu.adj.st/?adj_t=1hc3nwio',
    1: 'https://c2tu.adj.st/?adj_t=1hc3nwio',
    2: 'https://c2tu.adj.st/?adj_t=1hc3nwio',
    3: 'https://whep.adj.st/?adj_t=1h63h516',
    5: 'https://7y7p.adj.st/?adj_t=1hu8j3wa',
  }
  window.location.href = urlMap[zoneId] || urlMap[0]
}

export function web2appDownload() {
  const _url = window.adjustUrl
  if (platform === 'pc') {
    window.open(_url)
  } else {
    window.location.href = _url
  }
}

export function openTerms() {
  const _url = 'https://www.onemt.com/abroadgame/outer/terms'
  if (platform === 'pc') {
    window.open(_url)
  } else {
    window.location.href = _url
  }
}

export function openPolicy() {
  const _url = 'https://www.onemt.com/abroadgame/outer/policy'
  if (platform === 'pc') {
    window.open(_url)
  } else {
    window.location.href = _url
  }
}

export function toast(text: string, dialog = false, timeout = 1000) {
  emitter.emit('toast', { text, timeout, dialog })
}

export function loading() {
  emitter.emit('loading')
}

export function closeLoading() {
  emitter.emit('closeLoading')
}

export function $alert(content: string, e?: AlertEvent) {
  emitter.emit(
    'alert',
    Object.assign(
      {
        open: true,
        content,
      },
      e
    )
  )
}
